import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_PERSONALITY_DISORDER,
  PERSONALITY_TEST_ASSESSMENT_ID,
  traitNameKeyMap,
} from "../../utils/general-config";
import { personalityDisorderDetermineTraitLevel } from "../../utils/assessment-utils";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getCookie,
  getEnneagramBannerImage,
  getPersonalityDisorderImage,
} from "../../utils/utility-functions";
import ExpandableSection from "../../components/expandable-collapsible/component";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const PersonalityDisorderReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const [contentData, setContentData] = useState({});
  const { t } = useTranslation();
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const navigate = useNavigate();
  const bannerImage = getEnneagramBannerImage();
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_PERSONALITY_DISORDER[currentLocale] ||
            MODEL_TYPE_PERSONALITY_DISORDER[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("welcomeModal.take_the_test")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        id={reportType}
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}

        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />
        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />
        {/* Hero Content */}
        <div className="relative h-full flex items-center" id={reportType}>
          <div className="w-full  px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col  md:flex-row items-center md:items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const personalityDisorderImage1String = "personality-disorder-1";
  const personalityDisorderImage2String = "personality-disorder-2";

  const personalityDisorderImage1 = getPersonalityDisorderImage(
    personalityDisorderImage1String
  );

  const personalityDisorderImage2 = getPersonalityDisorderImage(
    personalityDisorderImage2String
  );

  const PersonalityTraits = ({ PersonalityTraitsData }) => {
    const understandingYourResults = PersonalityTraitsData;

    const understandingYourResultsRootHeading =
      understandingYourResults?.RootHeading;

    const words = understandingYourResultsRootHeading?.split(" ");
    const lastWord = words?.pop();
    const remainingText = words?.join(" ");
    return (
      <div className="">
        <h1 className="text-2xl font-bold">
          {t("report.personality_disorder.introduction")}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-8 items-center">
          {/* Text Section */}
          <div>
            <p className="text-gray-700 text-base md:text-lg leading-relaxed">
              {contentData?.intro}
            </p>
          </div>

          {/* Image Section */}
          <div className="flex justify-center items-center">
            <img
              className="w-full max-w-[350px] h-auto object-contain"
              src={personalityDisorderImage1}
              alt={personalityDisorderImage1String}
            />
          </div>
        </div>

        <h2 className="text-xl font-semibold mt-6">
          {remainingText} <span className="text-purple-600">{lastWord}</span>
        </h2>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {PersonalityTraitsData?.Content.map((item, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg ${
                index === 0
                  ? "bg-yellow-50 text-yellow-800"
                  : index === 1
                    ? "bg-purple-50 text-purple-800"
                    : index === 2
                      ? "bg-green-50 text-green-800"
                      : "bg-blue-50 text-blue-800"
              } shadow-md`}
            >
              <h3 className="text-lg font-bold">{item.Heading}</h3>
              <ul className="list-disc list-outside pl-5 text-gray-700">
                {item.Content.map((point, idx) => (
                  <li key={idx} className="text-gray-700">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const interpretingScoresData = contentData?.interpretingScores;

  const PersonalityScores = () => {
    function mapUserTraits(userTraits) {
      // Convert the user traits into a map for easy lookup
      const traitMap = userTraits.reduce((acc, trait) => {
        const key = trait.trait.title.trim().toLowerCase();
        const localeKey =
          traitNameKeyMap[currentLocale] || traitNameKeyMap[LOCALE.EN];
        acc[key] = {
          category: trait.trait[localeKey],
          score: trait.score,
          percentage: trait.percentage,
          level: personalityDisorderDetermineTraitLevel(trait.score)[
            currentLocale
          ],
        };
        return acc;
      }, {});

      // Return all traits
      return Object.values(traitMap);
    }
    return (
      <div className="bg-white overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center ">
          <div>
            <h1 className="text-2xl font-bold mb-4">
              {interpretingScoresData?.RootHeading}
            </h1>
            <p className="text-base mb-4">
              {interpretingScoresData?.FlavorText}
            </p>

            {/* Table */}
            <div className="w-full bg-white">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="p-3 text-left font-bold tracking-wider text-lg">
                      {interpretingScoresData?.TableHeaders[0]}
                    </th>
                    <th className="p-3 text-right font-bold tracking-wider text-lg">
                      {interpretingScoresData?.TableHeaders[1]}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {mapUserTraits(userTraits).map((trait, index) => (
                    <tr key={index} className="border-b border-gray-300">
                      <td className="p-3 text-sm font-medium text-gray-900">
                        {trait.category}
                      </td>
                      <td className="p-3 text-right text-sm font-semibold text-[#805fe6]">
                        {trait.level}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            {/* Placeholder for Image */}
            <img
              className="w-full max-h-80 object-contain mb-4"
              src={personalityDisorderImage2}
              alt={personalityDisorderImage2String}
            />
            {/* Explanation Section */}
            <div className="bg-blue-50 p-4 rounded-lg shadow-md mt-8">
              <h2 className="text-lg font-semibold mb-4">
                {t("report.personality_disorder.score_meaning_heading")}:
              </h2>
              <ul className="space-y-3 list-disc list-outside pl-5 text-gray-700">
                {interpretingScoresData?.Content.map((score, index) => (
                  <li key={index} className="text-gray-700">
                    <span className="font-semibold text-gray-900">
                      {score.Heading}:{" "}
                    </span>
                    {score.Content}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NextSteps = () => {
    return (
      <div className="flex flex-col mx-auto">
        <h1 className="text-xl font-bold mb-4 text-left">
          {" "}
          {contentData?.nextSteps?.RootHeading}
        </h1>
        <div className="grid sm:flex sm:space-x-4 gap-4">
          {contentData?.nextSteps?.Content?.map((item, index) => (
            <div
              key={index}
              className="bg-blue-50 p-4 rounded-lg shadow-md sm:w-1/2"
            >
              <h2 className="text-lg font-semibold mb-2">{item.Heading}</h2>
              <ul className="space-y-2 list-disc list-outside pl-5 text-gray-700">
                {item.Content.map((text, subIndex) => (
                  <li key={subIndex} className="text-gray-700">
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const desclaimerData = contentData?.disclaimer;

  const resourcesData = contentData?.resources;

  const importantNotes = contentData?.importantNotes;

  const Card = ({ title, children }) => {
    return (
      <div className="w-full md:w-1/3 p-4 flex flex-col">
        <h1 className="text-xl font-bold mb-4 text-left">{title}</h1>
        <div className="bg-gray-50 p-4 md:p-6 rounded-lg shadow-md flex-1 flex flex-col">
          <div className="h-full">{children}</div>
        </div>
      </div>
    );
  };

  const NotesSection = () => {
    return (
      <div className="flex flex-col md:flex-row md:flex-wrap w-full h-full items-stretch">
        {/* Important Notes */}
        <Card title={importantNotes?.RootHeading}>
          {importantNotes?.Content?.map((item, index) => (
            <p key={index} className="mb-2">
              <strong>{item?.Heading}: </strong>
              {item?.Content}
            </p>
          ))}
        </Card>

        {/* Resources */}
        <Card title={resourcesData?.RootHeading}>
          <p className="mb-3">{resourcesData?.FlavorText}</p>
          <ul className="space-y-2">
            {resourcesData?.Content?.map((item, index) => (
              <li key={index}>
                <strong>{item?.Heading}: </strong>
                <a
                  href={`https://${item?.Content}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#805fe6] underline"
                >
                  <strong>{item?.Content}</strong>
                </a>
              </li>
            ))}
          </ul>
        </Card>

        {/* Disclaimer */}
        <Card title={desclaimerData?.RootHeading}>
          <p>{desclaimerData?.Content}</p>
        </Card>
      </div>
    );
  };

  const CallToActionSection = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              id="personality-disorder-report-enneagram-cta-button"
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id="personality-disorder-report-disc-cta-button"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id="personality-disorder-report-personality-cta-button"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  {/* {renderHeroBanner(contentData.header, "blue")} */}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <PersonalityTraits
                      PersonalityTraitsData={contentData.understandingResults}
                    />
                  </div>
                  <div className="bg-white rounded-xl sm:p-8 shadow-md"></div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t(
                        "report.personality_disorder.interpretation_next_steps"
                      )}
                    />
                  }
                >
                  {/* {renderHeroBanner(contentData.header, "blue")} */}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <PersonalityScores />
                  </div>
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <NextSteps />
                  </div>
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <NotesSection />
                  </div>
                </ExpandableSection>
              </section>
              {CallToActionSection(contentData)}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PersonalityDisorderReport;
